import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ScrollText, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { UserChip } from "@/components/UserAvatar";
import { api } from "@/lib/api";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const ACTION = {
  login: { label: "Inicio de sesión", tone: "info" },
  create_user: { label: "Alta usuario", tone: "success" },
  update_user: { label: "Edición usuario", tone: "secondary" },
  delete_user: { label: "Baja usuario", tone: "destructive" },
  reset_password: { label: "Reseteo contraseña", tone: "warning" },
  change_password: { label: "Cambio contraseña", tone: "secondary" },
  update_role: { label: "Edición rol", tone: "secondary" },
};

const toneClass = {
  info: "bg-info/15 text-info", success: "bg-success/15 text-success",
  warning: "bg-warning/20 text-warning-foreground", destructive: "bg-destructive/15 text-destructive",
  secondary: "",
};

export default function AuditPage() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["audit", "all"],
    queryFn: async () => (await api.get("/audit?limit=200")).data,
  });
  const logs = data?.logs || [];

  return (
    <div className="space-y-5" data-testid="audit-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <ScrollText className="h-6 w-6 text-primary" /> Auditoría
          </h1>
          <p className="text-sm text-muted-foreground">Registro de acciones del sistema (quién, qué y cuándo).</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="audit-refresh">
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Actualizar
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Acción</TableHead>
                <TableHead>Detalle</TableHead>
                <TableHead className="w-[220px]">Usuario</TableHead>
                <TableHead className="w-[180px] text-right">Fecha</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}><TableCell colSpan={4}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
              ))}
              {!isLoading && logs.length === 0 && (
                <TableRow><TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">Sin eventos registrados.</TableCell></TableRow>
              )}
              {logs.map((l) => {
                const a = ACTION[l.action] || { label: l.action, tone: "secondary" };
                return (
                  <TableRow key={l.id} data-testid="audit-row">
                    <TableCell><Badge variant="secondary" className={toneClass[a.tone]}>{a.label}</Badge></TableCell>
                    <TableCell className="text-sm">{l.detail || "—"}</TableCell>
                    <TableCell className="text-sm">
                      {l.user_name
                        ? <div className="flex flex-col"><UserChip name={l.user_name} color={l.user_color} size="sm" /><span className="ml-7 text-[11px] text-muted-foreground">{l.user_email}</span></div>
                        : <span className="text-muted-foreground">{l.user_email || "—"}</span>}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums text-muted-foreground">{fmt(l.timestamp)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
